<template>
    <div class="sc-properties">
        <div class="font-weight-bold mb-1" style="font-size: 13px">Properties available in Quaro</div>
        <p class="mb-2" style="font-size: 13px; opacity: 0.85">
            Choose which Search Console properties Quaro may read. This applies to everyone in your company, to the Quaro AI chat, and to the API and MCP. Nothing can be queried until at least
            one property is enabled.
        </p>

        <div v-if="loading" class="d-flex py-4">
            <v-progress-circular indeterminate size="24" color="primary" class="mx-auto"></v-progress-circular>
        </div>
        <div v-else>
            <div v-if="!properties.length && !errorMessage" style="font-size: 13px">Your Google account has no verified Search Console properties.</div>
            <v-checkbox v-for="property in properties" :key="property.site_url" v-model="selected" :value="property.site_url" dense hide-details class="mt-0 mb-1">
                <template v-slot:label>
                    <span style="font-size: 14px">{{ property.site_url }}</span>
                    <v-chip v-if="property.suggested" x-small outlined color="primary" class="ml-2">matches your domain</v-chip>
                </template>
            </v-checkbox>
            <div v-if="errorMessage" class="mt-2 error--text" style="font-size: 13px">{{ errorMessage }}</div>
            <div v-if="properties.length" class="mt-3 d-flex align-center">
                <v-btn small rounded color="primary" :loading="saving" :disabled="!dirty" @click="save">Save</v-btn>
                <span v-if="!dirty && saved" class="ml-3" style="font-size: 13px; opacity: 0.8"><v-icon x-small color="primary">mdi-check</v-icon> Saved</span>
            </div>
        </div>
    </div>
</template>

<script>
    // Admin-Auswahl der Search-Console-Properties, die Quaro abfragen darf (App, Chat, API, MCP). Spricht nur die
    // searchinsights-API an (/gsc/properties, /gsc/properties/allow), nie die monitoring-API direkt.
    // Ohne bisherige Freigabe sind die zu den Firmendomains passenden Properties vorausgewählt - aktiv wird aber erst nach "Save".
    //
    // Events:
    //   saved(allowedProperties)    nach erfolgreichem Speichern
    //   notify(message, icon, color)
    import { describeSearchConsoleError, searchConsoleErrorCode } from "../utils/searchConsole"

    export default {
        name: "SearchConsoleProperties",
        data() {
            return {
                loading: false,
                saving: false,
                saved: false,
                properties: [],
                selected: [],
                allowed: [],
                errorMessage: ""
            }
        },
        computed: {
            dirty() {
                const a = [...this.selected].sort().join("\n")
                const b = [...this.allowed].sort().join("\n")
                return a !== b
            }
        },
        methods: {
            authBody() {
                // Das Backend prüft Session-Token, Firmenzugehörigkeit und Admin-Rolle (wie bei den 2FA-Einstellungen).
                return { user_id: localStorage.id, company_id: localStorage.company_id, token: localStorage.token }
            },
            post(path, body) {
                return this.$helpers.axiosPost(process.env.VUE_APP_APIURL + "/gsc" + path, { ...this.authBody(), ...body })
            },
            // Firmendomains nur als Vorschlagshilfe; ein Fehler hier ist egal.
            async companyDomains() {
                try {
                    const response = await this.$helpers.axiosPost(process.env.VUE_APP_APIURL + "/company/domains", { company_id: localStorage.company_id })
                    const list = JSON.parse(response.data.verification_domain)
                    return Array.isArray(list) ? list.filter((d) => typeof d === "string") : []
                } catch (error) {
                    return []
                }
            },
            async load() {
                this.loading = true
                this.errorMessage = ""
                try {
                    const response = await this.post("/properties", { domains: await this.companyDomains() })
                    this.properties = (response.data && response.data.properties) || []
                    this.allowed = this.properties.filter((p) => p.allowed).map((p) => p.site_url)
                    this.selected = this.allowed.length ? [...this.allowed] : this.properties.filter((p) => p.suggested).map((p) => p.site_url)
                } catch (error) {
                    this.errorMessage = describeSearchConsoleError(searchConsoleErrorCode(error))
                }
                this.loading = false
            },
            async save() {
                this.saving = true
                this.errorMessage = ""
                try {
                    await this.post("/properties/allow", { properties: this.selected })
                    this.allowed = [...this.selected]
                    this.saved = true
                    this.$emit("saved", this.allowed)
                    this.$emit("notify", "Search Console properties saved.", "mdi-check-circle", "primary")
                } catch (error) {
                    this.errorMessage = describeSearchConsoleError(searchConsoleErrorCode(error))
                }
                this.saving = false
            }
        },
        mounted() {
            this.load()
        }
    }
</script>

<style scoped></style>
